import type {
  LocationQuery,
  RouteLocation,
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteLocationNormalizedLoaded,
  Router,
} from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import type { App, Plugin } from 'vue';
import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import store from 'store2';
import routes from '@/router/routes';
import { RouteName } from '@/router/route-names';
import type { AuthorisationData } from '@/common/composition/useLoginData';
import AuthenticationGuard from './guards/authentication.guard';
import DienstverbandGuard from './guards/dienstverband.guard';

export type ZvdZRouteMeta = {
  mustBeLoggedIn: boolean;
  showNavigation: boolean;
  needsDienstverbandId: boolean;
  isAuthorised: (input: AuthorisationData) => boolean;
};

export type ZvdZRouteLocation = RouteLocation & {
  name: RouteName;
  meta: ZvdZRouteMeta;
};

export type ZvdZRouteLocationNormalized = RouteLocationNormalized & {
  name: RouteName;
  meta: ZvdZRouteMeta;
  children: ZvdZRouteLocationNormalized[];
};

export type ZvdZLocationRaw = RouteLocationRaw & { name: RouteName };

export type ZvdZResolvedRoute = ZvdZRouteLocation & {
  href: string;
};

type ZvdZRouter = Omit<Router, 'resolve'> & {
  resolve(
    to: ZvdZLocationRaw | string,
    currentLocation?: RouteLocationNormalizedLoaded,
  ): ZvdZResolvedRoute;
};

// Fixes issue of query parts not being cast to strings by Vue-router
// Client-side routing typing differs from type on refresh
const stringifyRouteQueryValues = (
  obj: ZvdZRouteLocationNormalized,
): ZvdZRouteLocationNormalized => {
  if (!obj.query) {
    return obj;
  }
  const query = Object.fromEntries(
    Object.entries(obj.query)
      .filter(([, val]) => val !== undefined)
      .map(([key, val]) => [key, `${val}`]),
  );
  return { ...obj, query };
};

export const router: ZvdZRouter = createRouter({
  history: createWebHistory(),
  routes,
}) as ZvdZRouter;

router.onError(error => {
  if (/ChunkLoadError:.*failed.|Loading.*chunk.*failed./i.test(error.message)) {
    const today = DateTime.now().toISODate();
    if (store.get('chunkLoadErrorReloadDate') === today) {
      return;
    }

    store.set('chunkLoadErrorReloadDate', today);
    window.location.reload();
  }
});

// Register guards
router.beforeEach(AuthenticationGuard);
router.beforeEach(DienstverbandGuard);

const routeRef = ref<ZvdZRouteLocationNormalized>(
  stringifyRouteQueryValues(
    router.currentRoute.value as ZvdZRouteLocationNormalized,
  ),
);
const previousRouteRef = ref<ZvdZRouteLocationNormalized>();

export const route = computed<ZvdZRouteLocationNormalized>(
  () => routeRef.value as ZvdZRouteLocationNormalized,
);
export const routeQuery = computed<LocationQuery>(() => routeRef.value.query);
export const previousRoute = computed<ZvdZRouteLocationNormalized | undefined>(
  () => previousRouteRef.value,
);

router.afterEach((to, from) => {
  routeRef.value = stringifyRouteQueryValues(to as ZvdZRouteLocationNormalized);
  previousRouteRef.value = from as ZvdZRouteLocationNormalized;
});

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    RouteName: { [key in RouteName]: RouteName };
    $zvdzRoute: ZvdZRouteLocationNormalized;
  }
}

export const plugin: Plugin = {
  install(app: App<Element>) {
    app.use(router);

    app.mixin({
      computed: {
        RouteName: () => RouteName,
        $zvdzRoute: () => route.value,
      },
    });
  },
};

export function isRouteAuthorised(
  to: ZvdZLocationRaw,
  autorisatieData: AuthorisationData,
): boolean {
  const cRoute = router.resolve(to);
  if (!cRoute) {
    throw new Error(`Invalid route "${to}"`);
  }
  return cRoute.meta.isAuthorised(autorisatieData);
}

export default router;
